/**
 * The Event record's stage button (2026-10-29, docs/event-stage-button.md): what its main half runs and what its
 * menu lists for where the event is now.
 *
 * The button only offers steps a person takes. Approved, Pending Acceptance and Confirmed follow approvals, the
 * client invite and the client's signature; Partially Filled through Returned follow the warehouse scans, which the
 * database recomputes, so the fulfillment steps open the scanner rather than set a stage. Lost and Cancel are in
 * every live stage's menu. Invoiced is final: only people who can unlock an invoiced event see Unlock, and while it
 * is unlocked, Cancel and Lock.
 *
 * Pure and dependency-free: the page derives the approval and confirmation facts it already shows and passes them in.
 */

const normalise = (value: unknown): string => String(value ?? '').trim().toLowerCase();

export type EventStageActionId =
  | 'submit_for_approval'
  | 'approve'
  | 'reject'
  | 'send_to_client'
  | 'resend_to_client'
  | 'confirm_without_signature'
  | 'request_confirmation_exception'
  | 'submit_budgetary'
  | 'begin_filling'
  | 'begin_shipping'
  | 'begin_returning'
  | 'submit_for_invoicing'
  | 'invoice'
  | 'unlock'
  | 'lock'
  | 'lose'
  | 'cancel'
  | 'revive';

export type EventStageAction = {
  id: EventStageActionId;
  label: string;
  /** A second line in the menu: where the step leads. */
  description?: string;
  /** Shown but can't run; the reason is the button's title. */
  disabledReason?: string;
  danger?: boolean;
};

export type EventStageButtonModel = {
  /** The main half. `id` null is a stage the viewer waits on ("Awaiting approval"), shown disabled. */
  primary: { id: EventStageActionId | null; label: string; disabledReason?: string };
  /** The other steps allowed from here. */
  steps: EventStageAction[];
  /** Lost and Cancel, under a line, in red. */
  exits: EventStageAction[];
};

export type EventStageButtonInput = {
  /** events.status */
  status: string | null | undefined;
  canEdit: boolean;
  /** Approval, sending and confirmation are open to this viewer (the approval card's own gate). */
  salesActionsAllowed: boolean;
  canSubmitForApproval: boolean;
  /** Approvals waiting on this viewer. */
  approvalsForViewer: number;
  /** Approvals waiting on someone else. */
  approvalsForOthers: number;
  internalApprovalsComplete: boolean;
  clientAssigned: boolean;
  confirmationComplete: boolean;
  /** A confirmation exception is waiting on a manager. */
  confirmationPending: boolean;
  /** How this event may be confirmed without the client's signature, or null where the site requires one. */
  signatureBypass: 'confirm' | 'request_exception' | null;
  /**
   * How this event ships: multi-show events through the scanner's Ship step; single-show delivery events ship every
   * filled item at once; single-show customer pickup records the signed handoff.
   */
  shipping: 'scanner' | 'ship_all' | 'handoff';
  /** Admins and accounting (InvoiceLockBanner's rule). */
  canManageInvoiceLock: boolean;
  invoiceUnlocked: boolean;
};

const MISSING_CLIENT = 'Assign a client to this event before sending it.';

const FILLING_STATUSES = new Set(['confirmed', 'picking', 'in_progress', 'partially_filled', 'filled', 'partially_shipped']);
const SHIPPING_STATUSES = new Set([...FILLING_STATUSES, 'shipped', 'on_rent', 'partially_returned', 'returning']);
/** Something has shipped once the event reaches Partially Shipped; the stage function ranks shipping above filling. */
const SHIPPED_STATUSES = new Set(['partially_shipped', 'shipped', 'on_rent', 'partially_returned', 'returning', 'returned']);
const FULFILLMENT_STATUSES = new Set([...SHIPPING_STATUSES, 'returned']);
const TERMINAL_STATUSES = new Set(['lost', 'cancelled', 'void']);

const SCANNER = 'Opens the scanner';
const ACTIONS: Record<EventStageActionId, EventStageAction> = {
  submit_for_approval: { id: 'submit_for_approval', label: 'Submit for approval' },
  approve: { id: 'approve', label: 'Approve' },
  reject: { id: 'reject', label: 'Reject…', description: 'With a note for the requester' },
  send_to_client: { id: 'send_to_client', label: 'Send to client' },
  resend_to_client: { id: 'resend_to_client', label: 'Resend to client' },
  confirm_without_signature: { id: 'confirm_without_signature', label: 'Confirm without signature' },
  request_confirmation_exception: { id: 'request_confirmation_exception', label: 'Request confirmation exception' },
  submit_budgetary: { id: 'submit_budgetary', label: 'Submit budgetary' },
  begin_filling: { id: 'begin_filling', label: 'Begin filling', description: SCANNER },
  begin_shipping: { id: 'begin_shipping', label: 'Begin shipping', description: SCANNER },
  begin_returning: { id: 'begin_returning', label: 'Begin returning', description: SCANNER },
  submit_for_invoicing: { id: 'submit_for_invoicing', label: 'Submit for invoicing', description: 'Moves to Ready to Invoice' },
  invoice: { id: 'invoice', label: 'Invoice' },
  unlock: { id: 'unlock', label: 'Unlock…', description: 'With a reason; relocks when you leave' },
  lock: { id: 'lock', label: 'Lock' },
  lose: { id: 'lose', label: 'Mark as lost…', danger: true },
  cancel: { id: 'cancel', label: 'Cancel event…', danger: true },
  revive: { id: 'revive', label: 'Revive', description: 'Returns the event to Quote' },
};

const step = (id: EventStageActionId, disabledReason?: string): EventStageAction =>
  (disabledReason ? { ...ACTIONS[id], disabledReason } : ACTIONS[id]);

const primaryOf = (action: EventStageAction): EventStageButtonModel['primary'] => ({
  id: action.id,
  label: action.label,
  ...(action.disabledReason ? { disabledReason: action.disabledReason } : {}),
});

const waiting = (label: string, disabledReason?: string): EventStageButtonModel['primary'] => ({
  id: null,
  label,
  ...(disabledReason ? { disabledReason } : {}),
});

const EXITS: EventStageAction[] = [ACTIONS.lose, ACTIONS.cancel];

const SHIPPING: Record<EventStageButtonInput['shipping'], EventStageAction> = {
  scanner: ACTIONS.begin_shipping,
  ship_all: { id: 'begin_shipping', label: 'Ship filled items', description: 'Marks every filled item shipped' },
  handoff: { id: 'begin_shipping', label: 'Hand to customer', description: "Records the customer's signed pickup" },
};

function fulfillmentModel(status: string, shipping: EventStageButtonInput['shipping']): Pick<EventStageButtonModel, 'primary' | 'steps'> {
  const action = (id: EventStageActionId) => (id === 'begin_shipping' ? SHIPPING[shipping] : ACTIONS[id]);
  const allowed: EventStageActionId[] = [];
  if (FILLING_STATUSES.has(status)) allowed.push('begin_filling');
  if (SHIPPING_STATUSES.has(status)) allowed.push('begin_shipping');
  if (SHIPPED_STATUSES.has(status)) allowed.push('begin_returning');
  allowed.push('submit_for_invoicing');
  const main: EventStageActionId = status === 'returned'
    ? 'submit_for_invoicing'
    : SHIPPED_STATUSES.has(status) && status !== 'partially_shipped'
      ? 'begin_returning'
      : status === 'filled' || status === 'partially_shipped'
        ? 'begin_shipping'
        : 'begin_filling';
  return { primary: primaryOf(action(main)), steps: allowed.filter(id => id !== main).map(action) };
}

function salesModel(input: EventStageButtonInput, status: string): Pick<EventStageButtonModel, 'primary' | 'steps'> {
  const sendBlocked = input.clientAssigned ? undefined : MISSING_CLIENT;
  const bypass = input.signatureBypass === 'request_exception'
    ? step('request_confirmation_exception')
    : input.signatureBypass === 'confirm' ? step('confirm_without_signature') : null;
  const budgetary = input.confirmationComplete || input.confirmationPending ? null : step('submit_budgetary', sendBlocked);
  const steps = (...items: Array<EventStageAction | null>) => items.filter((item): item is EventStageAction => Boolean(item));

  // The approval card's gate: the workspace is read-only for this viewer, or the header still reads a later stage.
  if (!input.salesActionsAllowed) return { primary: waiting('Read only', 'Approvals and sending are closed for this event.'), steps: [] };
  if (input.confirmationPending) return { primary: waiting('Awaiting confirmation review'), steps: [] };
  if (status === 'pending_acceptance') {
    return { primary: waiting('Awaiting client signature'), steps: steps(step('resend_to_client', sendBlocked), bypass) };
  }
  if (input.internalApprovalsComplete) {
    return { primary: primaryOf(step('send_to_client', sendBlocked)), steps: steps(bypass, budgetary) };
  }
  if (input.approvalsForViewer > 0) return { primary: primaryOf(ACTIONS.approve), steps: steps(ACTIONS.reject, budgetary) };
  if (input.approvalsForOthers > 0) return { primary: waiting('Awaiting approval'), steps: steps(budgetary) };
  if (input.canSubmitForApproval) return { primary: primaryOf(ACTIONS.submit_for_approval), steps: steps(budgetary) };
  return { primary: waiting('Checking approvals'), steps: steps(budgetary) };
}

/** The stage button for this event, or null for someone who can't change its stage. */
export function eventStageButtonModel(input: EventStageButtonInput): EventStageButtonModel | null {
  const status = normalise(input.status);
  if (!input.canEdit || !status) return null;

  if (TERMINAL_STATUSES.has(status)) return { primary: primaryOf(ACTIONS.revive), steps: [], exits: [] };

  // Closed comes from R2 and has no unlock; the invoice lock is Invoiced's alone (EventDetail's isInvoiceLocked).
  if (status === 'closed') return { primary: waiting('Closed · final'), steps: [], exits: [] };
  if (status === 'invoiced') {
    if (!input.canManageInvoiceLock) return { primary: waiting('Invoiced · final'), steps: [], exits: [] };
    if (input.invoiceUnlocked) return { primary: primaryOf(ACTIONS.lock), steps: [], exits: [ACTIONS.cancel] };
    return { primary: waiting('Invoiced · final'), steps: [ACTIONS.unlock], exits: [] };
  }

  if (status === 'ready_to_invoice') return { primary: primaryOf(ACTIONS.invoice), steps: [], exits: EXITS };

  if (FULFILLMENT_STATUSES.has(status)) return { ...fulfillmentModel(status, input.shipping), exits: EXITS };

  return { ...salesModel(input, status), exits: EXITS };
}

/** Every action the button can show, flattened, e.g. to find one by id. */
export const eventStageButtonActions = (model: EventStageButtonModel): EventStageAction[] => [...model.steps, ...model.exits];
